import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { readEmbedded } from '../utils/embedded';
import { Helmet } from 'react-helmet-async';
import { CatalogItem } from './CatalogItem';
import { toSlug, slugToCategory } from '../utils/categorySlug';
import CATEGORY_SEO from '../data/categorySeo.json';
import PAGE_SEO from '../data/pageSeo.json';
import ARTICLES from '../data/articles.json';
import { ContentSections, Faq } from './ContentSections';

// Same JSON that scripts/prerender.mjs writes into the static HTML.
const DEFAULT_SEO = PAGE_SEO.categories;

export const Categories = ({ products: loaded = [] }) => {
  const { slug } = useParams();

  // Докато Firestore не е отговорил, страницата ползва продуктите, вградени
  // в нея при генерирането (src/utils/embedded.js). Иначе мрежата с продукти
  // се появяваше след секунда-две и бутваше надолу текста и въпросите под нея —
  // най-голямото подскачане на сайта за Google (CLS 0.5).
  const [embedded] = useState(() => readEmbedded('sa-category'));
  const products = loaded.length
    ? loaded
    : (embedded?.slug === slug ? embedded.products : []);

  const allCategories = [...new Set(products.map(p => p.type).filter(Boolean))];
  const categoryName = slug ? slugToCategory(slug, allCategories) : null;

  // A slug that matches no real category used to fall through and render the
  // full catalog, so several URLs served byte-identical content and Google
  // collapsed them into one canonical. Products arrive asynchronously, so only
  // call a slug unknown once there is something to match it against.
  const isUnknownCategory = Boolean(slug) && products.length > 0 && !categoryName;

  const seo = (slug && CATEGORY_SEO[slug]) || DEFAULT_SEO;

  // Някои категории събират и продукти от други – „Коледни подаръци“ показва
  // и коледната чаша от „Чаши“. Същото правило е в scripts/prerender.mjs.
  const alsoMatching = seo.includeMatching ? new RegExp(seo.includeMatching, 'i') : null;
  const belongs = p => p.type === categoryName ||
    (alsoMatching && alsoMatching.test(`${p.type} ${p.name} ${p.description || ''}`));

  const filtered = categoryName
    ? products.filter(belongs)
    : (slug ? [] : products);
  const relatedArticle = seo.relatedArticle ? ARTICLES[seo.relatedArticle] : null;

  return (
    <>
      <Helmet>
        <title>{isUnknownCategory ? 'Категорията не е намерена | Stamsy Art' : seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        {isUnknownCategory
          ? <meta name="robots" content="noindex, follow" />
          : <link rel="canonical" href={`https://stamsyart.bg/categories${slug ? `/${slug}` : ''}`} />}
      </Helmet>

      <div className="container-fluid mt-5">
        <nav className="catalog-breadcrumb" style={{ paddingLeft: '12px' }}>
          <Link to="/">Начало</Link>
          <span> / </span>
          {slug
            ? <><Link to="/categories">Категории</Link><span> / </span><span>{seo.h1}</span></>
            : <span>Категории</span>}
        </nav>

        <h1 style={{ fontSize: '1.6rem', margin: '1rem 0 .75rem', paddingLeft: '12px' }}>
          {isUnknownCategory ? 'Категорията не е намерена' : seo.h1}
        </h1>

        {isUnknownCategory
          ? (
            <p style={{ paddingLeft: '12px', maxWidth: '760px' }}>
              Такава категория няма. Разгледай <Link to="/categories">всички категории</Link>{' '}
              или <Link to="/catalog">целия каталог</Link>.
            </p>
          )
          : seo.intro && (
            <p style={{ paddingLeft: '12px', maxWidth: '760px', color: '#6d5560' }}>
              {seo.intro}
            </p>
          )}

        <div className="row">
          {filtered.map((product, idx) => (
            <div
              key={product.id}
              className="col-6 col-md-4 col-lg-3 reveal-up"
              style={{ transitionDelay: `${(idx % 4) * 0.09}s` }}
            >
              <CatalogItem {...product} />
            </div>
          ))}
        </div>

        {!isUnknownCategory && (seo.sections || seo.faq) && (
          <div className="content-wrap" style={{ marginLeft: 0 }}>
            <ContentSections sections={seo.sections} />
            <Faq items={seo.faq} />
            {relatedArticle && (
              <div className="content-cta">
                <p>{relatedArticle.lead}</p>
                <Link to={`/blog/${seo.relatedArticle}`}>
                  Прочети: {relatedArticle.h1}
                </Link>
              </div>
            )}
          </div>
        )}

        {allCategories.length > 0 && (
          <nav style={{ padding: '2rem 12px' }}>
            <h2 style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Всички категории</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {allCategories.map(cat => {
                const catSlug = toSlug(cat);
                return (
                  <Link key={catSlug} to={`/categories/${catSlug}`} className="sa-chip cat-chip">
                    {CATEGORY_SEO[catSlug]?.h1 || cat}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </>
  );
};
